<template>
  <div class="login-container">
    <div class="login-form">
      <h2 class="login-title">系统登录</h2>

      <el-form
          ref="loginForm"
          :model="loginData"
          :rules="loginRules"
      >
        <!-- 账号 -->
        <el-form-item prop="username">
          <el-input
              v-model="loginData.username"
              placeholder="请输入账号"
              prefix-icon="el-icon-user"
              autocomplete="username"
              @keyup.enter="focusPassword"
          ></el-input>
        </el-form-item>

        <!-- 密码 -->
        <el-form-item prop="password">
          <el-input
              ref="passwordInput"
              v-model="loginData.password"
              placeholder="请输入密码"
              type="password"
              show-password
              prefix-icon="el-icon-lock"
              autocomplete="current-password"
              @keyup.enter="handleLogin"
          ></el-input>
        </el-form-item>

        <!-- 记住我 & 忘记密码 -->
        <div class="login-options">
          <el-checkbox v-model="rememberMe" @change="handleRememberChange">记住密码</el-checkbox>
          <el-link type="primary" @click="showForgot = true">忘记密码？</el-link>
        </div>

        <!-- 提交按钮 -->
        <el-form-item>
          <el-button
              type="primary"
              class="login-btn"
              :loading="loading"
              @click="handleLogin"
          >
            立即登录
          </el-button>
        </el-form-item>

        <!-- 登录错误提示 -->
        <el-alert
            v-if="loginError"
            :title="loginError"
            type="error"
            show-icon
            :closable="true"
            @close="loginError = ''"
            style="margin-bottom: 16px"
        />
      </el-form>
    </div>

    <!-- 忘记密码弹窗 -->
    <el-dialog v-model="showForgot" title="忘记密码" width="400px" :close-on-click-modal="true">
      <p>请联系系统管理员重置密码。</p>
      <template #footer>
        <el-button @click="showForgot = false">知道了</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios'
import Qs from 'qs'

// 记住密码加密密钥（简单XOR混淆，非安全加密，仅防止明文存储）
const REMEMBER_KEY = 'CRM_HAOMAI_2026'

export default {
  name: 'LoginPage',
  data() {
    return {
      loginData: {
        username: '',
        password: ''
      },
      loginRules: {
        username: [
          { required: true, message: '请输入账号', trigger: 'blur' },
          { min: 2, max: 50, message: '账号长度2-50个字符', trigger: 'blur' }
        ],
        password: [
          { required: true, message: '请输入密码', trigger: 'blur' },
          { min: 1, max: 100, message: '密码长度1-100个字符', trigger: 'blur' }
        ]
      },
      loading: false,
      rememberMe: false,
      showForgot: false,
      loginError: ''
    }
  },
  created() {
    // 从localStorage恢复记住的账号和密码
    this.loadRememberedCredentials()
  },
  methods: {
    // 聚焦密码框
    focusPassword() {
      this.$refs.passwordInput.focus()
    },

    // 加载记住的凭据
    loadRememberedCredentials() {
      const savedUsername = localStorage.getItem('crm_saved_username')
      const savedPwdEncrypted = localStorage.getItem('crm_saved_pwd')
      const savedRemember = localStorage.getItem('crm_remember_me')

      if (savedRemember === 'true') {
        this.rememberMe = true
        this.loginData.username = savedUsername || ''
        if (savedPwdEncrypted) {
          this.loginData.password = this.simpleDecrypt(savedPwdEncrypted)
        }
      } else {
        this.rememberMe = false
        this.loginData.username = savedUsername || ''
      }
    },

    // 记住密码变化处理
    handleRememberChange(val) {
      if (!val) {
        // 取消记住：清除存储的密码
        localStorage.removeItem('crm_saved_pwd')
        localStorage.removeItem('crm_remember_me')
        localStorage.setItem('crm_remember_me', 'false')
      }
    },

    // 简单加密（XOR + Base64，防止明文存储密码）
    simpleEncrypt(text) {
      if (!text) return ''
      let result = ''
      for (let i = 0; i < text.length; i++) {
        const charCode = text.charCodeAt(i) ^ REMEMBER_KEY.charCodeAt(i % REMEMBER_KEY.length)
        result += String.fromCharCode(charCode)
      }
      return btoa(unescape(encodeURIComponent(result)))
    },

    // 简单解密
    simpleDecrypt(encoded) {
      if (!encoded) return ''
      try {
        const decoded = decodeURIComponent(escape(atob(encoded)))
        let result = ''
        for (let i = 0; i < decoded.length; i++) {
          const charCode = decoded.charCodeAt(i) ^ REMEMBER_KEY.charCodeAt(i % REMEMBER_KEY.length)
          result += String.fromCharCode(charCode)
        }
        return result
      } catch (e) {
        console.warn('解密记住的密码失败，清除存储')
        localStorage.removeItem('crm_saved_pwd')
        return ''
      }
    },

    // 保存凭据到localStorage
    saveCredentials() {
      if (this.rememberMe) {
        localStorage.setItem('crm_saved_username', this.loginData.username)
        localStorage.setItem('crm_saved_pwd', this.simpleEncrypt(this.loginData.password))
        localStorage.setItem('crm_remember_me', 'true')
      } else {
        localStorage.setItem('crm_saved_username', this.loginData.username)
        localStorage.removeItem('crm_saved_pwd')
        localStorage.setItem('crm_remember_me', 'false')
      }
    },

    // 登录
    async handleLogin() {
      // 表单校验
      try {
        await this.$refs.loginForm.validate()
      } catch (e) {
        return
      }

      this.loading = true
      this.loginError = ''

      try {
        const res = await axios({
          url: '/apiserve/crmapi/logincheck.asp',
          method: 'post',
          transformRequest: [function (data) {
            return Qs.stringify(data)
          }],
          data: {
            username: this.loginData.username,
            password: this.loginData.password
          }
        })

        // 解析响应（后端返回纯JSON，非标准JSON可能需要容错）
        let data
        const raw = res.data
        if (typeof raw === 'string') {
          // 检测是否为HTML响应（IIS错误页/重定向页）
          if (raw.trim().startsWith('<')) {
            console.error('Server returned HTML instead of JSON:', raw.substring(0, 200))
            throw new Error('SERVER_HTML_RESPONSE')
          }
          // 尝试JSON5/宽松解析
          try {
            data = JSON.parse(raw)
          } catch (e1) {
            // 兼容旧格式 "error0|username|password"
            if (raw.startsWith('error')) {
              data = { error: 'auth_failed', message: '账号或密码错误' }
            } else {
              // 尝试修复非标准JSON（单引号→双引号）
              try {
                const fixed = raw.replace(/'/g, '"')
                data = JSON.parse(fixed)
              } catch (e2) {
                console.error('Failed to parse response:', raw.substring(0, 200))
                throw new Error('JSON_PARSE_ERROR')
              }
            }
          }
        } else {
          data = raw
        }

        if (data.result === 'done') {
          // 登录成功
          localStorage.setItem('userid', data.userid)
          localStorage.setItem('token', data.token)
          localStorage.setItem('username', data.username)
          if (data.role) localStorage.setItem('role', data.role)

          // 保存记住密码
          this.saveCredentials()

          // 跳转
          const redirect = this.$route.query.redirect || '/'
          this.$router.replace(redirect)
        } else {
          // 登录失败
          this.loginData.password = ''

          if (data.error === 'rate_limit') {
            this.loginError = data.message || '尝试次数过多，请5分钟后再试'
          } else if (data.error === 'account_disabled') {
            this.loginError = data.message || '账号已被禁用，请联系管理员'
          } else {
            this.loginError = data.message || '账号或密码错误'
          }

          // 登录失败时清除记住的密码
          if (this.rememberMe) {
            localStorage.removeItem('crm_saved_pwd')
          }
        }
      } catch (error) {
        console.error('Login error:', error)

        // 检查是否是 HTML 响应错误（API 路径不对或 IIS 配置缺失）
        if (error.message === 'SERVER_HTML_RESPONSE') {
          this.loginError = '服务器配置异常：API 返回了HTML而非JSON。请检查服务器上 /apiserve 虚拟目录是否正确配置。'
          return
        }
        if (error.message === 'JSON_PARSE_ERROR') {
          this.loginError = '服务器返回了无法解析的数据，请联系管理员检查API服务。'
          return
        }

        let message = '登录失败，请检查网络连接'
        if (error.response) {
          switch (error.response.status) {
            case 401:
              message = '账号或密码错误'
              this.loginData.password = ''
              break
            case 429:
              message = '尝试次数过多，请稍后再试'
              break
            case 500:
              message = '服务器错误，请联系管理员'
              break
          }
        }
        this.loginError = message
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.login-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: linear-gradient(
      135deg,
      #1a2332 0%,
      #2d4a6a 50%,
      #1a2332 100%
  );
  position: relative;
  overflow: hidden;
}

.login-container::before {
  content: '';
  position: absolute;
  width: 200%;
  height: 200%;
  top: -50%;
  left: -50%;
  background: radial-gradient(
      circle at 30% 50%,
      rgba(64, 158, 255, 0.08) 0%,
      transparent 50%
  );
  animation: float 20s ease-in-out infinite;
}

@keyframes float {
  0%, 100% { transform: translateY(0) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(2deg); }
}

.login-form {
  width: 420px;
  padding: 48px 40px;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  position: relative;
  z-index: 1;
}

.login-title {
  text-align: center;
  margin-bottom: 36px;
  font-size: 26px;
  font-weight: 600;
  color: #1a2332;
  letter-spacing: 2px;
}

.login-options {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.login-btn {
  width: 100%;
  height: 44px;
  font-size: 16px;
  letter-spacing: 4px;
  border-radius: 8px;
}

/* 表单项间距 */
:deep(.el-form-item) {
  margin-bottom: 22px;
}

/* checkbox样式 */
:deep(.el-checkbox__label) {
  color: #606266;
  font-size: 14px;
}

/* 密码输入框 */
:deep(.el-input__password) {
  letter-spacing: 2px;
}
</style>
